/**
 * Las direcciones viejas del Admin y a dónde llevan hoy: la ÚNICA tabla.
 *
 * Una dirección vieja no caduca nunca. Las alarmas guardan su enlace al
 * abrirse y no se reescribe (`alarm_engine`, «only if null»), el Registro
 * guarda los suyos hasta diez años, y hay marcadores, avisos de ntfy y
 * documentos que los citan. Por eso esto no es código heredado que haya que
 * borrar: es el enrutado de lo que el Admin dijo alguna vez.
 *
 * `App.tsx` lo consulta en cada cambio de dirección, antes de pintar ninguna
 * página, así que las páginas no llevan alias propios. Cuando una pestaña o
 * una sección cambia de sitio, su línea va AQUÍ.
 *
 * Lógica pura (sin React, sin DOM, sin `@shared`): la prueba
 * `tenant/frontend/hooks/adminLegacyRoutes.test.ts` la importa por ruta
 * relativa, porque el admin no tiene ejecutor de pruebas.
 */

/** Pestañas de Configuración (o secciones, por `?section=`) que salieron a
 *  otra página. */
const CONFIG_MOVED_OUT: Readonly<Record<string, string>> = {
  // Salieron de Configuración el 2026-09-27.
  recurring_tasks: '/procesos?tab=tareas',
  countries: '/paises',
  local_agent: '/versiones-agente?tab=canales',
  ntfy_deliveries: '/monitor?tab=entregas',
  test_runner: '/test',
  // Del 2026-10-25 (paso 3 de la reorganización): lo que se toca a diario va
  // a la página que controla.
  workshop_defaults: '/talleres?tab=por-defecto',
  billing: '/suscripciones?tab=impago', // la pestaña «Ciclo de vida del taller»
  lifecycle: '/suscripciones?tab=impago',
  fx: '/paises?tab=monedas',
  marketplaces: '/procesos?tab=marketplaces', // «Tiendas → Canales»
  // El grupo «Monitor y avisos» entero, a Monitor → Ajustes.
  notifications: '/monitor?tab=ajustes&sub=ntfy',
  ntfy_server: '/monitor?tab=ajustes&sub=ntfy',
  ntfy_channels: '/monitor?tab=ajustes&sub=ntfy',
  ntfy_rules: '/monitor?tab=ajustes&sub=ntfy',
  monitor: '/monitor?tab=ajustes&sub=alarmas', // la pestaña «Alarmas y retención»
  monitor_policy: '/monitor?tab=ajustes&sub=alarmas',
  workshop_notices: '/monitor?tab=ajustes&sub=avisos-taller',
  migration: '/migraciones?tab=ajustes',
};

/** Pestañas de Configuración que cambiaron de nombre. */
const CONFIG_RENAMED_TABS: Readonly<Record<string, string>> = {
  // Sentry era una sección de «Healthcheck».
  healthcheck: 'sentry',
};

/** Ficha del taller: pestañas de organizaciones anteriores → la que las absorbió. */
const TALLER_TABS: Readonly<Record<string, string>> = {
  general: 'cuenta',
  branding: 'cuenta',
  // «Facturación» (suscripción + límites) se partió en dos el 2026-09-13.
  facturacion: 'suscripcion',
  suscripciones: 'suscripcion',
  miembros: 'equipo',
  // De nueve pestañas a siete (2026-10-25): Impresoras + Tiendas son
  // «Conexiones» y Monitor + Migraciones son «Actividad». (La «Actividad» de
  // antes de la fase 3 de Monitor pasó a «Monitor» y ahora vuelve a llamarse
  // así: el nombre viejo ya cae en su sitio.)
  agentes: 'conexiones',
  impresoras: 'conexiones',
  tiendas: 'conexiones',
  eventos: 'actividad',
  monitor: 'actividad',
  migraciones: 'actividad',
};

/** Estadísticas: lo que se fue a Monitor con la fase 1. */
const ANALYTICS_TO_MONITOR: Readonly<Record<string, string>> = {
  audit: 'registro',
  alarms: 'alarmas',
  sentry: 'errores',
};

/** Pestañas que cambiaron de nombre dentro de su misma página. */
const SAME_PAGE_TABS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // «Avisos» de Monitor se llama «Entregas» desde el 2026-10-25.
  '/monitor': { avisos: 'entregas' },
  // La pestaña «Flota» pasó a llamarse «Agentes».
  '/impresoras': { flota: 'agentes' },
};

/** Las pestañas de Impresoras que salieron a Versiones del agente (2026-10-25). */
const TO_AGENT_VERSIONS = new Set(['canales', 'versiones']);

function withParams(path: string, params: URLSearchParams): string {
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}

/**
 * A dónde lleva hoy `pathname` + `search`, o `null` si la dirección ya es la
 * buena. Lo que no se traduce (filtros, `?event=`, `?section=`) se conserva.
 */
export function legacyRedirect(pathname: string, search: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const params = new URLSearchParams(search);
  const tab = params.get('tab');

  switch (path) {
    // Invitaciones fue una página, luego una pestaña de Talleres, y desde el
    // 2026-10-25 una pestaña de Usuarios.
    case '/invitaciones':
      return '/usuarios?tab=invitaciones';
    case '/talleres':
      if (tab === 'invitaciones') return '/usuarios?tab=invitaciones';
      return null;
    case '/estadisticas':
      return withParams('/analytics', params);
    // «Workers» es una pestaña de Procesos.
    case '/workers':
      return '/procesos';
    // «Agente local» pasó a ser «Impresoras» (su «Flota» es «Agentes»), y sus
    // canales y versiones tienen página propia.
    case '/agente-local':
      if (tab === 'flota' || tab === 'agentes') return '/impresoras?tab=agentes';
      if (tab === 'impresoras') return '/impresoras?tab=impresoras';
      return `/versiones-agente?tab=${tab === 'versiones' ? 'versiones' : 'canales'}`;
    case '/impresoras':
      if (tab && TO_AGENT_VERSIONS.has(tab)) return `/versiones-agente?tab=${tab}`;
      break;
    // Planes y Límites tienen página propia.
    case '/suscripciones':
      if (tab === 'planes' || tab === 'limites') return `/planes?tab=${tab}`;
      return null;
    case '/analytics': {
      const moved = tab ? ANALYTICS_TO_MONITOR[tab] : undefined;
      return moved ? `/monitor?tab=${moved}` : null;
    }
    case '/configuracion': {
      const section = params.get('section');
      const moved = (section && CONFIG_MOVED_OUT[section]) || (tab && CONFIG_MOVED_OUT[tab]);
      if (moved) return moved;
      const renamed = tab ? CONFIG_RENAMED_TABS[tab] : undefined;
      if (renamed) {
        params.set('tab', renamed);
        return withParams(path, params);
      }
      return null;
    }
    default:
      break;
  }

  const renamed = tab ? SAME_PAGE_TABS[path]?.[tab] : undefined;
  if (renamed) {
    params.set('tab', renamed);
    return withParams(path, params);
  }

  if (/^\/talleres\/[^/]+$/.test(path) && tab && TALLER_TABS[tab]) {
    params.set('tab', TALLER_TABS[tab]);
    return withParams(path, params);
  }

  return null;
}
